/* eslint-disable max-len */
/* istanbul ignore file */
import * as Yup from 'yup';
import { AnyObject } from 'yup/lib/types';
import { VALIDATION_MESSAGE } from '../../ValidationMessage';
import { DateSchemaType, ExtendedDateSchemaType } from '../ValidationTypes';

enum DATE_VALIDATION_KEYS {
  COME_AFTER_DATE_OR_EQUAL = 'mustComeAfterOrEqualSpecifiedDate',
  VALID_DATE_FORMAT = 'validDateFormat',
  VALID_DATE_CHARACTERS = 'validDateCharacters',
}

export const AddCustomDateValidation = (): void => {
  Yup.addMethod(
    Yup.date,
    DATE_VALIDATION_KEYS.COME_AFTER_DATE_OR_EQUAL,
    function (compareDateFieldName, compareDisplayName, errorMessage) {
      return this.test(
        DATE_VALIDATION_KEYS.COME_AFTER_DATE_OR_EQUAL,
        errorMessage,
        function (value) {
          const { parent, path, createError } =
            this as Yup.TestContext<AnyObject>;
          const compareValue = parent[compareDateFieldName];

          let endDateBeforeBegin = null;
          const data = value as unknown as Date;
          if (compareValue && data && compareValue > data) {
            // Signifies that the error we are looking for has occured
            endDateBeforeBegin = null;
          } else {
            return true;
          }

          return (
            endDateBeforeBegin !== null ||
            createError({
              path,
              message:
                errorMessage ||
                VALIDATION_MESSAGE.BEFORE_OR_EQUAL_TO_DATE(compareDisplayName),
            })
          );
        }
      );
    }
  );

  // Helper function to convert mm/dd/yyyy format to ISO format (YYYY-MM-DD)
  const convertDateStringToISO = (value: unknown): unknown => {
    if (typeof value !== 'string' || value === '') {
      return value;
    }

    // Handle ISO datetime strings (e.g., "2024-11-12T00:00:00" or "2024-11-12T00:00:00.000Z")
    // Extract just the date portion (YYYY-MM-DD)
    const isoDateTimeMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (isoDateTimeMatch) {
      const year = parseInt(isoDateTimeMatch[1], 10);
      const month = parseInt(isoDateTimeMatch[2], 10);
      const day = parseInt(isoDateTimeMatch[3], 10);

      if (
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= new Date(year, month, 0).getDate() &&
        year >= 1000 &&
        year <= 2999
      ) {
        return `${year}-${String(month).padStart(2, '0')}-${String(
          day
        ).padStart(2, '0')}`;
      }
    }

    if (/^(\d{4})-(\d{2})-(\d{2})$/.test(value)) {
      return value;
    }

    const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const match = value.match(slashPattern) || value.match(dashPattern);

    if (match) {
      const month = parseInt(match[1], 10);
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      if (
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= new Date(year, month, 0).getDate() &&
        year >= 1000 &&
        year <= 2999
      ) {
        return `${year}-${String(month).padStart(2, '0')}-${String(
          day
        ).padStart(2, '0')}`;
      }
    }

    return value;
  };

  Yup.addMethod(
    Yup.date,
    DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
    function (errorMessage) {
      return this.transform(function (value, originalValue) {
        return convertDateStringToISO(
          originalValue !== undefined ? originalValue : value
        );
      }).test(
        DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
        errorMessage,
        function (value) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { path, createError, parent } =
            this as Yup.TestContext<AnyObject>;

          if (value === null || value === undefined) {
            return true;
          }

          if (value instanceof Date) {
            if (!isNaN(value.getTime())) {
              return true;
            }
          }

          const valueAsUnknown = value as unknown;
          if (typeof valueAsUnknown === 'string') {
            const stringValue: string = valueAsUnknown;

            if (stringValue === '') {
              return true;
            }

            const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (isoMatch) {
              const year = parseInt(isoMatch[1], 10);
              const month = parseInt(isoMatch[2], 10);
              const day = parseInt(isoMatch[3], 10);
              const date = new Date(year, month - 1, day);
              if (
                !isNaN(date.getTime()) &&
                month >= 1 &&
                month <= 12 &&
                day >= 1 &&
                day <= new Date(year, month, 0).getDate() &&
                year >= 1000 &&
                year <= 2999
              ) {
                return true;
              }
            }

            const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
            const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
            const match =
              stringValue.match(slashPattern) || stringValue.match(dashPattern);

            if (!match) {
              return createError({
                path,
                message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
              });
            }

            const month = parseInt(match[1], 10);
            const day = parseInt(match[2], 10);
            const year = parseInt(match[3], 10);
            const daysInMonth = new Date(year, month, 0).getDate();

            if (
              !(
                month >= 1 &&
                month <= 12 &&
                day >= 1 &&
                day <= daysInMonth &&
                year >= 1000 &&
                year <= 2999
              )
            ) {
              return createError({
                path,
                message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
              });
            }

            return true;
          }

          return createError({
            path,
            message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
          });
        }
      );
    }
  );

  Yup.addMethod(
    Yup.mixed,
    DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
    function (errorMessage) {
      return this.test(
        DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
        errorMessage,
        function (value) {
          const { path, createError } = this as Yup.TestContext<AnyObject>;

          if (value === null || value === undefined) {
            return true;
          }

          if (value instanceof Date) {
            if (!isNaN(value.getTime())) {
              return true;
            }
          }

          if (typeof value === 'string') {
            const stringValue: string = value;

            if (stringValue === '') {
              return true;
            }

            const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (isoMatch) {
              const year = parseInt(isoMatch[1], 10);
              const month = parseInt(isoMatch[2], 10);
              const day = parseInt(isoMatch[3], 10);
              const date = new Date(year, month - 1, day);
              if (
                !isNaN(date.getTime()) &&
                month >= 1 &&
                month <= 12 &&
                day >= 1 &&
                day <= new Date(year, month, 0).getDate() &&
                year >= 1000 &&
                year <= 2999
              ) {
                return true;
              }
            }

            const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
            const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
            const match =
              stringValue.match(slashPattern) || stringValue.match(dashPattern);

            if (!match) {
              return createError({
                path,
                message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
              });
            }

            const month = parseInt(match[1], 10);
            const day = parseInt(match[2], 10);
            const year = parseInt(match[3], 10);
            const daysInMonth = new Date(year, month, 0).getDate();

            if (
              !(
                month >= 1 &&
                month <= 12 &&
                day >= 1 &&
                day <= daysInMonth &&
                year >= 1000 &&
                year <= 2999
              )
            ) {
              return createError({
                path,
                message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
              });
            }

            return true;
          }

          return createError({
            path,
            message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
          });
        }
      );
    }
  );

  Yup.addMethod(
    Yup.string,
    DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
    function (errorMessage) {
      return this.test(
        DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
        errorMessage,
        function (value) {
          const { path, createError } = this as Yup.TestContext<AnyObject>;

          if (!value || value === '') {
            return true;
          }

          const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (isoMatch) {
            const year = parseInt(isoMatch[1], 10);
            const month = parseInt(isoMatch[2], 10);
            const day = parseInt(isoMatch[3], 10);
            const date = new Date(year, month - 1, day);
            if (
              !isNaN(date.getTime()) &&
              month >= 1 &&
              month <= 12 &&
              day >= 1 &&
              day <= new Date(year, month, 0).getDate() &&
              year >= 1000 &&
              year <= 2999
            ) {
              return true;
            }
          }

          const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
          const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
          const match = value.match(slashPattern) || value.match(dashPattern);

          if (!match) {
            return createError({
              path,
              message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
            });
          }

          const month = parseInt(match[1], 10);
          const day = parseInt(match[2], 10);
          const year = parseInt(match[3], 10);
          const daysInMonth = new Date(year, month, 0).getDate();

          if (
            !(
              month >= 1 &&
              month <= 12 &&
              day >= 1 &&
              day <= daysInMonth &&
              year >= 1000 &&
              year <= 2999
            )
          ) {
            return createError({
              path,
              message: errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
            });
          }

          return true;
        }
      );
    }
  );

  Yup.addMethod(
    Yup.string,
    DATE_VALIDATION_KEYS.VALID_DATE_CHARACTERS,
    function (errorMessage) {
      return this.test(
        DATE_VALIDATION_KEYS.VALID_DATE_CHARACTERS,
        errorMessage,
        function (value) {
          const { path, createError } = this as Yup.TestContext<AnyObject>;

          if (!value || value === '') {
            return true;
          }

          // Allow ISO datetime format (e.g., "2024-11-12T00:00:00" or "2024-11-12T00:00:00.000Z")
          const isoDateTimePattern =
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/;
          if (isoDateTimePattern.test(value)) {
            return true;
          }

          const validCharacterPattern = /^[0-9\/\-]*$/;
          if (!validCharacterPattern.test(value)) {
            return createError({
              path,
              message:
                errorMessage || VALIDATION_MESSAGE.DATE_INVALID_CHARACTERS,
            });
          }

          return true;
        }
      );
    }
  );

  Yup.addMethod(
    Yup.mixed,
    DATE_VALIDATION_KEYS.VALID_DATE_CHARACTERS,
    function (errorMessage) {
      return this.test(
        DATE_VALIDATION_KEYS.VALID_DATE_CHARACTERS,
        errorMessage,
        function (value) {
          const { path, createError } = this as Yup.TestContext<AnyObject>;

          if (value === null || value === undefined || value === '') {
            return true;
          }

          if (value instanceof Date) {
            return true;
          }

          if (typeof value === 'string') {
            // Allow ISO datetime format (e.g., "2024-11-12T00:00:00" or "2024-11-12T00:00:00.000Z")
            const isoDateTimePattern =
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/;
            if (isoDateTimePattern.test(value)) {
              return true;
            }

            const validCharacterPattern = /^[0-9\/\-]*$/;
            if (!validCharacterPattern.test(value)) {
              return createError({
                path,
                message:
                  errorMessage || VALIDATION_MESSAGE.DATE_INVALID_CHARACTERS,
              });
            }
          }

          return true;
        }
      );
    }
  );
};

export const convertDateStringToISO = (value: unknown): unknown => {
  if (typeof value !== 'string' || value === '') {
    return value;
  }

  // Handle ISO datetime strings (e.g., "2024-11-12T00:00:00" or "2024-11-12T00:00:00.000Z")
  // Extract just the date portion (YYYY-MM-DD)
  const isoDateTimeMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoDateTimeMatch) {
    const year = parseInt(isoDateTimeMatch[1], 10);
    const month = parseInt(isoDateTimeMatch[2], 10);
    const day = parseInt(isoDateTimeMatch[3], 10);

    if (
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= new Date(year, month, 0).getDate() &&
      year >= 1000 &&
      year <= 2999
    ) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}`;
    }
  }

  if (/^(\d{4})-(\d{2})-(\d{2})$/.test(value)) {
    return value;
  }

  const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
  const match = value.match(slashPattern) || value.match(dashPattern);

  if (match) {
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    if (
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= new Date(year, month, 0).getDate() &&
      year >= 1000 &&
      year <= 2999
    ) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}`;
    }
  }

  return value;
};

export const createDateSchema = (): ExtendedDateSchemaType => {
  const schema = Yup.mixed()
    .transform(function (value, originalValue) {
      if (value === null || value === undefined || value === '') {
        return value;
      }

      const converted = convertDateStringToISO(
        originalValue !== undefined ? originalValue : value
      );

      if (
        typeof converted === 'string' &&
        /^(\d{4})-(\d{2})-(\d{2})$/.test(converted)
      ) {
        const date = new Date(converted);
        return isNaN(date.getTime()) ? converted : date;
      }
      if (converted instanceof Date) {
        return converted;
      }
      return converted;
    })
    .test(
      DATE_VALIDATION_KEYS.VALID_DATE_CHARACTERS,
      VALIDATION_MESSAGE.DATE_INVALID_CHARACTERS,
      function (value) {
        const { createError } = this as Yup.TestContext<AnyObject>;
        if (value === null || value === undefined || value === '') return true;
        if (value instanceof Date) return true;

        if (typeof value === 'string') {
          // Allow ISO datetime format (e.g., "2024-11-12T00:00:00" or "2024-11-12T00:00:00.000Z")
          // as it will be transformed to date-only format
          const isoDateTimePattern =
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/;
          if (isoDateTimePattern.test(value)) {
            return true;
          }

          const validCharacterPattern = /^[0-9\/\-]*$/;
          if (!validCharacterPattern.test(value)) {
            return createError({
              message: VALIDATION_MESSAGE.DATE_INVALID_CHARACTERS,
            });
          }
        }

        return true;
      }
    )
    .test('is-date', VALIDATION_MESSAGE.MUST_BE_VALID_DATE, function (value) {
      const { createError } = this as Yup.TestContext<AnyObject>;
      if (value === null || value === undefined || value === '') return true;
      if (value instanceof Date) {
        return !isNaN(value.getTime());
      }
      // For non-Date values (like strings), we need to validate them
      // If it's a string, check if it's a valid date format
      if (typeof value === 'string') {
        // Empty strings are handled above, so this must be a non-empty string
        // Check if it matches a valid date pattern
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
          const year = parseInt(isoMatch[1], 10);
          const month = parseInt(isoMatch[2], 10);
          const day = parseInt(isoMatch[3], 10);
          const date = new Date(year, month - 1, day);
          if (
            !isNaN(date.getTime()) &&
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            day <= new Date(year, month, 0).getDate() &&
            year >= 1000 &&
            year <= 2999
          ) {
            return true;
          }
        }
        const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
        const match = value.match(slashPattern) || value.match(dashPattern);
        if (match) {
          const month = parseInt(match[1], 10);
          const day = parseInt(match[2], 10);
          const year = parseInt(match[3], 10);
          const daysInMonth = new Date(year, month, 0).getDate();
          if (
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            day <= daysInMonth &&
            year >= 1000 &&
            year <= 2999
          ) {
            return true;
          }
        }
        // If it's a string but doesn't match valid date patterns, fail validation
        return createError({
          message: VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
        });
      }
      // For other types, fail validation
      return createError({
        message: VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      });
    })
    .test(
      DATE_VALIDATION_KEYS.VALID_DATE_FORMAT,
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      function (value) {
        const { path, createError } = this as Yup.TestContext<AnyObject>;

        if (value === null || value === undefined) {
          return true;
        }

        if (value instanceof Date) {
          if (!isNaN(value.getTime())) {
            return true;
          }
        }

        if (typeof value === 'string') {
          const stringValue: string = value;

          if (stringValue === '') {
            return true;
          }

          const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (isoMatch) {
            const year = parseInt(isoMatch[1], 10);
            const month = parseInt(isoMatch[2], 10);
            const day = parseInt(isoMatch[3], 10);
            const date = new Date(year, month - 1, day);
            if (
              !isNaN(date.getTime()) &&
              month >= 1 &&
              month <= 12 &&
              day >= 1 &&
              day <= new Date(year, month, 0).getDate() &&
              year >= 1000 &&
              year <= 2999
            ) {
              return true;
            }
          }

          const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
          const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
          const match =
            stringValue.match(slashPattern) || stringValue.match(dashPattern);

          if (!match) {
            return createError({
              path,
              message: VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
            });
          }

          const month = parseInt(match[1], 10);
          const day = parseInt(match[2], 10);
          const year = parseInt(match[3], 10);
          const daysInMonth = new Date(year, month, 0).getDate();

          if (
            !(
              month >= 1 &&
              month <= 12 &&
              day >= 1 &&
              day <= daysInMonth &&
              year >= 1000 &&
              year <= 2999
            )
          ) {
            return createError({
              path,
              message: VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
            });
          }

          return true;
        }

        return createError({
          path,
          message: VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
        });
      }
    );

  const addCustomMethods = (s: DateSchemaType): ExtendedDateSchemaType => {
    (s as unknown as ExtendedDateSchemaType).max = function (
      maxDate: Date | string,
      message?: string
    ) {
      const self = this as DateSchemaType;
      const result = self.test(
        'max-date',
        message || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
        function (
          this: Yup.TestContext<AnyObject>,
          value: Date | string | null | undefined
        ) {
          if (value === null || value === undefined || value === '')
            return true;
          if (!(value instanceof Date)) return true;
          const max = maxDate instanceof Date ? maxDate : new Date(maxDate);
          return (
            value <= max ||
            this.createError({
              message: message || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
            })
          );
        }
      );
      return addCustomMethods(result);
    };

    (s as unknown as ExtendedDateSchemaType).min = function (
      minDate: Date | string | { path: string },
      message?: string
    ) {
      const self = this as DateSchemaType;
      const result = self.test(
        'min-date',
        message || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
        function (
          this: Yup.TestContext<AnyObject>,
          value: Date | string | null | undefined
        ) {
          if (value === null || value === undefined || value === '')
            return true;
          if (!(value instanceof Date)) return true;

          let min: Date;
          if (minDate && typeof minDate === 'object' && 'path' in minDate) {
            const refValue = this.parent[minDate.path];
            if (!refValue) return true;
            min = refValue instanceof Date ? refValue : new Date(refValue);
          } else {
            min = minDate instanceof Date ? minDate : new Date(minDate);
          }

          return (
            value >= min ||
            this.createError({
              message: message || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
            })
          );
        }
      );
      return addCustomMethods(result);
    };

    (s as unknown as ExtendedDateSchemaType).mustComeAfterOrEqualSpecifiedDate =
      function (
        compareDateFieldName: string,
        compareDisplayName: string,
        errorMessage?: string
      ) {
        const self = this as DateSchemaType;
        const result = self.test(
          DATE_VALIDATION_KEYS.COME_AFTER_DATE_OR_EQUAL,
          errorMessage || VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
          function (
            this: Yup.TestContext<AnyObject>,
            value: Date | string | null | undefined
          ) {
            const { parent, path, createError } = this;
            const compareValue = parent[compareDateFieldName];

            let endDateBeforeBegin = null;
            const data = value as unknown as Date;
            if (compareValue && data && compareValue > data) {
              // Signifies that the error we are looking for has occured
              endDateBeforeBegin = null;
            } else {
              return true;
            }

            return (
              endDateBeforeBegin !== null ||
              createError({
                path,
                message:
                  errorMessage ||
                  VALIDATION_MESSAGE.BEFORE_OR_EQUAL_TO_DATE(
                    compareDisplayName
                  ),
              })
            );
          }
        );
        return addCustomMethods(result);
      };

    const originalWhen = s.when;
    // Override when method to ensure it returns ExtendedDateSchemaType
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s as any).when = function (
      this: DateSchemaType,
      keys: string | string[],
      builder: (
        value: Date | string | null | undefined,
        schema: DateSchemaType
      ) => DateSchemaType
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = originalWhen.call(this, keys, builder as any);
      return addCustomMethods(result);
    };

    const originalRequired = s.required;
    s.required = function (message?: string) {
      const self = this as DateSchemaType;
      let result = originalRequired.call(self, message);

      result = result.test(
        'required-empty-string',
        message || VALIDATION_MESSAGE.REQUIRED,
        function (
          this: Yup.TestContext<AnyObject>,
          value: Date | string | null | undefined
        ) {
          if (value === '') {
            return this.createError({
              message: message || VALIDATION_MESSAGE.REQUIRED,
            });
          }
          return true;
        }
      );

      return addCustomMethods(result);
    };

    return s as ExtendedDateSchemaType;
  };

  return addCustomMethods(schema as DateSchemaType);
};
